import { refreshAccessToken, RefreshNetworkError } from './authCodeFlow';
import { hostBridge } from '../messaging/hostBridge';
import type { AuthMode, SetTokensInput } from './AuthContext';

const REFRESH_AT_FRACTION = 0.75;
const RETRY_DELAY_MS = 3000;
const PERMANENT_FAILURE_STATUSES = [401, 403];

export interface TokenRefreshDependencies {
    getRefreshToken: () => string | null;
    getMode: () => AuthMode;
    setTokens: (tokens: SetTokensInput) => void;
    clearAuth: () => void;
    onSessionExpired: () => void;
    /**
     * Invoked when a refresh attempt fails because the `fetch` itself threw
     * (offline/DNS/connection-refused) rather than the server rejecting it.
     * The caller must report this to the network-status reconnect mechanism so
     * the confirmed-reconnect signal re-triggers the refresh — otherwise the
     * timer is cleared here and nothing would ever re-attempt it (API-4800).
     */
    onNetworkDeferred: () => void;
}

let timerId: ReturnType<typeof setTimeout> | null = null;

function clearTimer() {
    if (timerId !== null) {
        clearTimeout(timerId);
        timerId = null;
    }
}

function isPermanentFailure(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    return PERMANENT_FAILURE_STATUSES.some(status =>
        error.message.includes(`(${status})`)
    );
}

/**
 * True when `refreshAccessToken`'s fetch itself failed (offline, DNS,
 * connection refused) rather than the server rejecting the request. Must
 * never be escalated to a session-expiry — the terminal should keep
 * punching offline and re-attempt the refresh once connectivity returns.
 */
function isNetworkFailure(error: unknown): boolean {
    return error instanceof RefreshNetworkError;
}

async function executeRefresh(
    deps: TokenRefreshDependencies,
    isRetry: boolean
): Promise<void> {
    const refreshToken = deps.getRefreshToken();
    if (!refreshToken) {
        handlePermanentFailure(deps);
        return;
    }

    try {
        const response = await refreshAccessToken(refreshToken);

        const newRefreshToken = response.refresh_token ?? refreshToken;
        const expiresAt = new Date(
            Date.now() + response.expires_in * 1000
        ).toISOString();

        deps.setTokens({
            accessToken: response.access_token,
            refreshToken: newRefreshToken,
            expiresAt,
            mode: deps.getMode()
        });

        if (deps.getMode() === 'webview') {
            hostBridge.sessionToken({ refreshToken: newRefreshToken });
        }

        scheduleRefresh(response.expires_in, deps);
    } catch (error: unknown) {
        if (isPermanentFailure(error)) {
            // The server was reachable and rejected the refresh — the
            // session is genuinely no longer valid.
            handlePermanentFailure(deps);
        } else if (isNetworkFailure(error)) {
            // Offline/connectivity failure — we never reached the server, so
            // this says nothing about the session's validity. Defer instead
            // of expiring or spinning a bare retry timer. Clearing the timer
            // alone is not enough: nothing would ever fire again because the
            // app still believes it is online. Report the failure so the
            // network-status layer flips to offline and its confirmed-reconnect
            // signal re-triggers a fresh attempt once connectivity returns.
            clearTimer();
            deps.onNetworkDeferred();
        } else if (isRetry) {
            handlePermanentFailure(deps);
        } else {
            timerId = setTimeout(() => {
                void executeRefresh(deps, true);
            }, RETRY_DELAY_MS);
        }
    }
}

function handlePermanentFailure(deps: TokenRefreshDependencies) {
    clearTimer();
    deps.onSessionExpired();
}

export function scheduleRefresh(
    expiresInSeconds: number,
    deps: TokenRefreshDependencies
): void {
    clearTimer();
    const delayMs = Math.max(
        expiresInSeconds * REFRESH_AT_FRACTION * 1000,
        1000
    );
    timerId = setTimeout(() => {
        void executeRefresh(deps, false);
    }, delayMs);
}

export function cancelRefresh(): void {
    clearTimer();
}
